import Autocomplete, { type AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import {
  PERMISSION_GRANTEE_SEARCH_MAXIMUM_LENGTH,
  type PermissionGranteeOption,
  mergePermissionGranteeOptions,
  usePermissionGranteeTypeahead,
} from "permissions/_views/search/use-permission-grantee-typeahead";
import { type HTMLAttributes, type Key, useState } from "react";

/**
 * A search for members of the viewer's organization by name or email, for the record page: one person
 * for Check access, several for Add people. Both read the coworker search the other permission
 * dialogs use, so a person is found the same way everywhere.
 */

type Typeahead = ReturnType<typeof usePermissionGranteeTypeahead>;

function noOptionsText(typeahead: Typeahead): string {
  if (typeahead.isSearchTextTooLong) return `Use ${PERMISSION_GRANTEE_SEARCH_MAXIMUM_LENGTH} characters or fewer`;
  return typeahead.isSearchReady ? "No people found" : "Type at least 2 characters";
}

function renderSearchInput(params: AutocompleteRenderInputParams, typeahead: Typeahead, label: string) {
  let helperText: string | undefined;
  if (typeahead.isError) helperText = "Unable to search people.";
  else if (typeahead.isSearchTextTooLong) helperText = `Search must be ${PERMISSION_GRANTEE_SEARCH_MAXIMUM_LENGTH} characters or fewer.`;
  return (
    <TextField
      {...params}
      size="small"
      error={typeahead.isError || typeahead.isSearchTextTooLong}
      helperText={helperText}
      label={label}
      placeholder="Type a name or email"
      slotProps={{
        ...params.slotProps,
        htmlInput: { ...params.slotProps.htmlInput, maxLength: PERMISSION_GRANTEE_SEARCH_MAXIMUM_LENGTH },
      }}
    />
  );
}

function renderOption(optionProps: HTMLAttributes<HTMLLIElement> & { key?: Key }, option: PermissionGranteeOption) {
  const { key, ...rest } = optionProps;
  return (
    <li key={key} {...rest}>
      <ListItemText primary={option.label} secondary={option.secondaryLabel} />
    </li>
  );
}

export function PersonPicker(
  props: Readonly<{
    label: string;
    value: PermissionGranteeOption | null;
    onChange: (option: PermissionGranteeOption | null) => void;
  }>,
) {
  const [input, setInput] = useState("");
  const typeahead = usePermissionGranteeTypeahead(input);
  const options = props.value ? mergePermissionGranteeOptions([props.value], typeahead.options) : typeahead.options;
  return (
    <Autocomplete<PermissionGranteeOption, false, false, false>
      sx={{ flex: "1 1 260px", minWidth: 0 }}
      filterOptions={(all) => all}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      inputValue={input}
      onInputChange={(_event, value, reason) => {
        if (reason === "input" || reason === "clear") setInput(value);
        if (reason === "selectOption") setInput(value);
      }}
      loading={typeahead.isFetching}
      loadingText="Searching people..."
      noOptionsText={noOptionsText(typeahead)}
      options={options}
      value={props.value}
      onChange={(_event, option) => props.onChange(option)}
      renderInput={(params) => renderSearchInput(params, typeahead, props.label)}
      renderOption={renderOption}
    />
  );
}

export function PeoplePicker(
  props: Readonly<{
    label: string;
    value: readonly PermissionGranteeOption[];
    onChange: (options: PermissionGranteeOption[]) => void;
    disabled?: boolean;
  }>,
) {
  const [input, setInput] = useState("");
  const typeahead = usePermissionGranteeTypeahead(input);
  return (
    <Autocomplete<PermissionGranteeOption, true, false, false>
      multiple
      disabled={props.disabled}
      filterOptions={(all) => all}
      filterSelectedOptions
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      inputValue={input}
      onInputChange={(_event, value, reason) => {
        if (reason === "input" || reason === "clear") setInput(value);
      }}
      loading={typeahead.isFetching}
      loadingText="Searching people..."
      noOptionsText={noOptionsText(typeahead)}
      options={mergePermissionGranteeOptions(props.value, typeahead.options)}
      value={[...props.value]}
      onChange={(_event, options) => props.onChange(options)}
      renderInput={(params) => renderSearchInput(params, typeahead, props.label)}
      renderOption={renderOption}
    />
  );
}
